<template>
  <v-container fluid>
    <v-card class="pa-3 mb-3">
      <v-layout row wrap align-center>
        <v-flex xs12 sm8 class="px-2">
          <v-text-field v-model="search" :label='$t("carOwnersPage.searchLabel")' append-icon="search"
            :loading="loading" @keyup.enter="fetch" @click:append="fetch" />
        </v-flex>
        <v-flex xs12 sm4 class="px-2 text-xs-end">
          <v-btn color="primary" @click="openCreate">{{ $t("carOwnersPage.newOwner") }}</v-btn>
        </v-flex>
      </v-layout>
    </v-card>

    <v-card>
      <v-data-table :headers="headers" :items="owners" :loading="loading" :total-items="total"
        :pagination.sync="pagination" class="elevation-0">
        <template slot="items" slot-scope="props">
          <td>{{ props.item.id }}</td>
          <td>{{ props.item.name }}</td>
          <td>{{ props.item.account_code || '-' }}</td>
          <td>{{ props.item.phone_number }}</td>
          <!-- Cars = DISTINCT plates (a car shared by several drivers counts
               once); the Drivers link opens the pre-filtered Drivers page -->
          <td>{{ props.item.cars_count }}</td>
          <td>
            <a
              v-if="props.item.drivers_count > 0"
              class="cars-link"
              @click="goToDrivers(props.item)"
            >{{ props.item.drivers_count }}&nbsp;<v-icon small color="primary">open_in_new</v-icon></a>
            <span v-else>{{ props.item.drivers_count || 0 }}</span>
          </td>
          <td><strong>{{ formatNum(props.item.balance) }} {{ $t("carOwnersPage.syp") }}</strong></td>
          <td>
            <v-btn small flat color="primary" @click="openEdit(props.item)">{{ $t("carOwnersPage.edit") }}</v-btn>
            <v-btn small flat color="green" @click="openSettle(props.item)" :disabled="props.item.balance <= 0">{{ $t("carOwnersPage.settle") }}</v-btn>
            <v-btn small flat @click="openShares(props.item)">{{ $t("carOwnersPage.shares") }}</v-btn>
          </td>
        </template>
        <template slot="no-data">
          <div class="text-xs-center pa-4 grey--text">{{ $t("carOwnersPage.noOwners") }}</div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create / edit -->
    <v-dialog v-model="formDialog" max-width="480">
      <v-card class="pa-3">
        <div class="title mb-2">{{ editing ? $t("carOwnersPage.editOwner") : $t("carOwnersPage.newOwner") }}</div>
        <v-text-field v-model="form.name" :label='$t("carOwnersPage.name")' />
        <v-text-field v-model="form.phone_number" :label='$t("carOwnersPage.phoneNumber")' />
        <v-text-field v-model="form.notes" :label='$t("carOwnersPage.notes")' />
        <div class="text-xs-end">
          <v-btn flat @click="formDialog = false">{{ $t("carOwnersPage.cancel") }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">{{ $t("carOwnersPage.save") }}</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Settle -->
    <v-dialog v-model="settleDialog" max-width="420">
      <v-card class="pa-3" v-if="settleOwner">
        <div class="title mb-2">{{ $t("carOwnersPage.settleTitle", { name: settleOwner.name }) }}</div>
        <div class="grey--text mb-2">{{ $t("carOwnersPage.balance") }}: {{ formatNum(settleOwner.balance) }} {{ $t("carOwnersPage.syp") }}</div>
        <v-text-field v-model.number="settleAmount" type="number" :label='$t("carOwnersPage.amountToPayOut")' />
        <v-text-field v-model="settleNotes" :label='$t("carOwnersPage.notes")' />
        <div class="text-xs-end">
          <v-btn flat @click="settleDialog = false">{{ $t("carOwnersPage.cancel") }}</v-btn>
          <v-btn color="green" dark :loading="saving" @click="doSettle">{{ $t("carOwnersPage.payOut") }}</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Shares -->
    <v-dialog v-model="sharesDialog" max-width="900">
      <v-card class="pa-3" v-if="sharesOwner">
        <div class="title mb-2">{{ $t("carOwnersPage.sharesTitle", { name: sharesOwner.name }) }}</div>
        <v-layout row wrap align-center>
          <v-flex xs12 sm3 class="px-2">
            <choose-date :label='$t("carOwnersPage.fromDate")' clearable v-model="sharesFrom"></choose-date>
          </v-flex>
          <v-flex xs12 sm3 class="px-2">
            <choose-date :label='$t("carOwnersPage.toDate")' clearable v-model="sharesTo"></choose-date>
          </v-flex>
          <v-flex xs12 sm6 class="px-2 text-xs-end">
            <v-btn small color="primary" :loading="sharesLoading" @click="loadShares">{{ $t("carOwnersPage.applyFilter") }}</v-btn>
            <v-btn small color="green" dark :loading="sharesExporting" @click="exportSharesExcel">{{ $t("carOwnersPage.exportExcel") }}</v-btn>
          </v-flex>
        </v-layout>
        <v-data-table :headers="shareHeaders" :items="shares" :loading="sharesLoading" hide-actions class="elevation-0">
          <template slot="items" slot-scope="props">
            <td>{{ props.item.booking_id || '—' }}</td>
            <td>{{ props.item.driver_name }}</td>
            <td>{{ props.item.car_ref || '—' }}</td>
            <td :style="{ color: props.item.amount < 0 ? 'red' : 'green' }">{{ formatNum(props.item.amount) }}</td>
            <td>{{ formatDate(props.item.created_at) }}</td>
          </template>
          <template slot="no-data"><div class="text-xs-center pa-3 grey--text">{{ $t("carOwnersPage.noShares") }}</div></template>
        </v-data-table>

        <div v-if="sharesTotals" class="mt-3">
          <div class="subheading font-weight-bold mb-2">{{ $t("carOwnersPage.totalsTitle") }}</div>
          <v-layout row wrap>
            <v-flex xs12 sm6 class="px-2" v-if="sharesTotals.daily.length">
              <div class="body-2 grey--text">{{ $t("carOwnersPage.dailyTotals") }}</div>
              <table class="totals-table">
                <tr v-for="(row, i) in sharesTotals.daily" :key="'day-' + i">
                  <td>{{ totalRowLabel(row) }}</td>
                  <td class="text-xs-end">{{ formatNum(totalRowAmount(row)) }}</td>
                </tr>
              </table>
            </v-flex>
            <v-flex xs12 sm6 class="px-2" v-if="sharesTotals.monthly.length">
              <div class="body-2 grey--text">{{ $t("carOwnersPage.monthlyTotals") }}</div>
              <table class="totals-table">
                <tr v-for="(row, i) in sharesTotals.monthly" :key="'month-' + i">
                  <td>{{ totalRowLabel(row) }}</td>
                  <td class="text-xs-end">{{ formatNum(totalRowAmount(row)) }}</td>
                </tr>
              </table>
            </v-flex>
          </v-layout>
          <div class="subheading font-weight-bold mt-2" v-if="sharesTotals.grand != null">
            {{ $t("carOwnersPage.grandTotal") }}: {{ formatNum(sharesTotals.grand) }} {{ $t("carOwnersPage.syp") }}
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ChooseDate from "@/components/monaco/input/ChooseDate";
import { companyToday, formatCompanyTime } from "@/util/companyTime";

export default {
  name: "CarOwners",
  title: "carOwnersPage.pageTitle",
  components: { ChooseDate },
  data() {
    return {
      search: "",
      loading: false,
      owners: [],
      total: 0,
      pagination: { rowsPerPage: 10, page: 1 },
      formDialog: false,
      editing: null,
      saving: false,
      form: { name: "", phone_number: "", notes: "" },
      settleDialog: false,
      settleOwner: null,
      settleAmount: null,
      settleNotes: "",
      sharesDialog: false,
      sharesOwner: null,
      shares: [],
      sharesLoading: false,
      sharesFrom: null,
      sharesTo: null,
      sharesTotals: null,
      sharesExporting: false,
    };
  },
  computed: {
    headers() {
      return [
        { text: "#", value: "id", sortable: false },
        { text: this.$t("carOwnersPage.name"), value: "name", sortable: false },
        { text: this.$t("carOwnersPage.accountCode"), value: "account_code", sortable: false },
        { text: this.$t("carOwnersPage.phone"), value: "phone_number", sortable: false },
        { text: this.$t("carOwnersPage.cars"), value: "cars_count", sortable: false },
        { text: this.$t("carOwnersPage.drivers"), value: "drivers_count", sortable: false },
        { text: this.$t("carOwnersPage.balance"), value: "balance", sortable: false },
        { text: "", value: "actions", sortable: false },
      ];
    },
    shareHeaders() {
      return [
        { text: this.$t("carOwnersPage.booking"), value: "booking_id", sortable: false },
        { text: this.$t("carOwnersPage.driver"), value: "driver_name", sortable: false },
        { text: this.$t("carOwnersPage.carRef"), value: "car_ref", sortable: false },
        { text: this.$t("carOwnersPage.amount"), value: "amount", sortable: false },
        { text: this.$t("carOwnersPage.date"), value: "created_at", sortable: false },
      ];
    },
  },
  watch: {
    pagination: { handler() { this.fetch(); }, deep: true },
  },
  mounted() { this.fetch(); },
  methods: {
    formatNum(v) { return v == null ? "—" : this.formatMoney(v); },
    // QA: jump to the Drivers page pre-filtered to captains driving this
    // owner's cars (server-side car_owner_id filter; owner name for the chip).
    goToDrivers(owner) {
      this.$router.push({
        path: "/drivers",
        query: { car_owner_id: String(owner.id), car_owner_name: owner.name },
      });
    },
    // Final-version doc 2026-10-05: company clock (UTC+3), not the PC's zone.
    formatDate(d) { return formatCompanyTime(d, "YYYY-MM-DD HH:mm:ss", "—"); },
    fetch() {
      this.loading = true;
      this.axios.get("car-owner", {
        params: { search: this.search, perPage: this.pagination.rowsPerPage, page: this.pagination.page },
      }).then(({ data }) => {
        const p = data.data || {};
        this.owners = p.data || [];
        this.total = p.total || this.owners.length;
      }).catch(() => {
        window.getApp.$emit("SHOW_TOAST", this.$t("carOwnersPage.failedLoad"), "red");
      }).finally(() => (this.loading = false));
    },
    openCreate() {
      this.editing = null;
      this.form = { name: "", phone_number: "", notes: "" };
      this.formDialog = true;
    },
    openEdit(owner) {
      this.editing = owner;
      this.form = { name: owner.name, phone_number: owner.phone_number, notes: owner.notes };
      this.formDialog = true;
    },
    save() {
      if (!this.form.name) { window.getApp.$emit("SHOW_TOAST", this.$t("carOwnersPage.nameRequired"), "red"); return; }
      this.saving = true;
      const req = this.editing
        ? this.axios.put(`car-owner/${this.editing.id}`, this.form)
        : this.axios.post("car-owner", this.form);
      req.then(() => {
        window.getApp.$emit("SHOW_TOAST", this.$t("carOwnersPage.saved"), "green");
        this.formDialog = false;
        this.fetch();
      }).catch((e) => {
        window.getApp.$emit("SHOW_TOAST", (e.response && e.response.data && e.response.data.message) || this.$t("carOwnersPage.failed"), "red");
      }).finally(() => (this.saving = false));
    },
    openSettle(owner) {
      this.settleOwner = owner;
      this.settleAmount = null;
      this.settleNotes = "";
      this.settleDialog = true;
    },
    doSettle() {
      if (!this.settleAmount || this.settleAmount <= 0) { window.getApp.$emit("SHOW_TOAST", this.$t("carOwnersPage.enterAmount"), "red"); return; }
      this.saving = true;
      this.axios.post(`car-owner/${this.settleOwner.id}/settle`, { amount: this.toStoredAmount(this.settleAmount), notes: this.settleNotes })
        .then(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("carOwnersPage.paidOut"), "green");
          this.settleDialog = false;
          this.fetch();
        }).catch((e) => {
          window.getApp.$emit("SHOW_TOAST", (e.response && e.response.data && e.response.data.message) || this.$t("carOwnersPage.failed"), "red");
        }).finally(() => (this.saving = false));
    },
    openShares(owner) {
      this.sharesOwner = owner;
      this.shares = [];
      this.sharesFrom = null;
      this.sharesTo = null;
      this.sharesTotals = null;
      this.sharesDialog = true;
      this.loadShares();
    },
    sharesParams(extra) {
      const params = Object.assign({}, extra);
      if (this.sharesFrom) params.from = this.sharesFrom;
      if (this.sharesTo) params.to = this.sharesTo;
      return params;
    },
    loadShares() {
      if (!this.sharesOwner) return;
      if (this.sharesFrom && this.sharesTo && this.sharesFrom > this.sharesTo) {
        window.getApp.$emit("SHOW_TOAST", this.$t("carOwnersPage.fromBeforeTo"), "red");
        return;
      }
      this.sharesLoading = true;
      this.axios.get(`car-owner/${this.sharesOwner.id}/shares`, { params: this.sharesParams({ perPage: 50 }) })
        .then(({ data }) => { this.shares = (data.data && data.data.data) || []; })
        .catch(() => {})
        .finally(() => (this.sharesLoading = false));
      this.loadShareTotals();
    },
    loadShareTotals() {
      if (!this.sharesOwner) return;
      this.sharesTotals = null;
      this.axios.get(`car-owner/${this.sharesOwner.id}/shares`, { params: this.sharesParams({ totals: 1 }) })
        .then(({ data }) => { this.sharesTotals = this.normalizeTotals((data && data.data) || {}); })
        .catch(() => { this.sharesTotals = null; });
    },
    normalizeTotals(payload) {
      const totals = payload.totals || payload || {};
      const daily = totals.daily || [];
      const monthly = totals.monthly || [];
      let grand = totals.grand_total;
      if (grand == null) grand = totals.grand;
      if (grand == null) grand = totals.total;
      if (!daily.length && !monthly.length && grand == null) return null;
      return { daily, monthly, grand };
    },
    totalRowLabel(row) {
      return row.date || row.day || row.month || row.period || "";
    },
    totalRowAmount(row) {
      if (row.total != null) return row.total;
      if (row.amount != null) return row.amount;
      return row.sum;
    },
    // Fetches EVERY row of the filtered range (page loop), not just the 50
    // shown in the dialog, so the export never silently truncates history.
    // ownerId + baseParams are captured ONCE at click time so a mid-export
    // owner/filter change cannot mix two owners' rows; resolves null (abort)
    // if the dialog's owner changes while pages are still loading.
    fetchAllShares(ownerId, baseParams) {
      const perPage = 500;
      const rows = [];
      const fetchPage = (page) =>
        this.axios
          .get(`car-owner/${ownerId}/shares`, { params: Object.assign({}, baseParams, { perPage, page }) })
          .then(({ data }) => {
            if (!this.sharesOwner || this.sharesOwner.id !== ownerId) return null;
            const p = (data && data.data) || {};
            (p.data || []).forEach((r) => rows.push(r));
            const lastPage = Number(p.last_page || 1);
            if (page < lastPage && page < 200) return fetchPage(page + 1);
            return rows;
          });
      return fetchPage(1);
    },
    exportFileStamp() {
      return companyToday();
    },
    exportSharesExcel() {
      if (!this.sharesOwner) return;
      if (this.sharesFrom && this.sharesTo && this.sharesFrom > this.sharesTo) {
        window.getApp.$emit("SHOW_TOAST", this.$t("carOwnersPage.fromBeforeTo"), "red");
        return;
      }
      this.sharesExporting = true;
      const isArabic = this.$i18n && this.$i18n.locale === "ar";
      // Snapshot owner + filters + totals at click time; the page loop and
      // the written file must not read live state that can change mid-export.
      const ownerId = this.sharesOwner.id;
      const ownerName = this.sharesOwner.name;
      const exportParams = this.sharesParams();
      const totalsSnapshot = this.sharesTotals;

      Promise.all([
        this.fetchAllShares(ownerId, exportParams),
        import(/* webpackChunkName: "xlsx" */ "xlsx-js-style"),
      ])
        .then(
          function (results) {
            const rows = results[0];
            if (rows == null) return; // aborted: dialog owner changed mid-export
            const XLSX = results[1].default || results[1];
            // Stored amounts carry the pre-redenomination scale; exports show
            // the new currency (÷100), same as the money mixin.
            const money = (value) => Number(value || 0) / 100;
            const totals = totalsSnapshot;

            const header = [
              this.$t("carOwnersPage.booking"),
              this.$t("carOwnersPage.driver"),
              this.$t("carOwnersPage.carRef"),
              this.$t("carOwnersPage.amount"),
              this.$t("carOwnersPage.date"),
            ];
            const data = rows.map(
              function (row) {
                return [
                  row.booking_id == null ? "-" : String(row.booking_id),
                  row.driver_name || "-",
                  row.car_ref || "-",
                  money(row.amount),
                  this.formatDate(row.created_at),
                ];
              }.bind(this)
            );

            const sectionRows = [];
            const boldRows = [];
            let cursor = data.length + 1;
            const pushRow = (cells, bold) => {
              sectionRows.push(cells);
              if (bold) boldRows.push(cursor);
              cursor++;
            };
            if (totals) {
              pushRow(["", "", "", "", ""], false);
              if (totals.daily.length) {
                pushRow([this.$t("carOwnersPage.dailyTotals"), "", "", "", ""], true);
                totals.daily.forEach((row) =>
                  pushRow([this.totalRowLabel(row), "", "", money(this.totalRowAmount(row)), ""], false)
                );
              }
              if (totals.monthly.length) {
                pushRow([this.$t("carOwnersPage.monthlyTotals"), "", "", "", ""], true);
                totals.monthly.forEach((row) =>
                  pushRow([this.totalRowLabel(row), "", "", money(this.totalRowAmount(row)), ""], false)
                );
              }
              if (totals.grand != null) {
                pushRow([this.$t("carOwnersPage.grandTotal"), "", "", money(totals.grand), ""], true);
              }
            }

            const ws = XLSX.utils.aoa_to_sheet([header].concat(data, sectionRows));
            ws["!cols"] = [
              { wch: 12 },
              { wch: 28 },
              { wch: 16 },
              { wch: 16 },
              { wch: 22 },
            ];

            const thin = { style: "thin", color: { rgb: "9E9E9E" } };
            const borders = { top: thin, bottom: thin, left: thin, right: thin };
            const lastRowIndex = data.length + sectionRows.length;
            for (let r = 0; r <= lastRowIndex; r++) {
              for (let c = 0; c < header.length; c++) {
                const cell = ws[XLSX.utils.encode_cell({ r: r, c: c })];
                if (!cell) {
                  continue;
                }
                if (r === 0) {
                  cell.s = {
                    font: { bold: true },
                    fill: { patternType: "solid", fgColor: { rgb: "ECEFF1" } },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                  continue;
                }
                const bold = boldRows.indexOf(r) !== -1;
                const style = {
                  font: { bold: bold },
                  border: borders,
                  alignment: { vertical: "center" },
                };
                if (bold) {
                  style.fill = { patternType: "solid", fgColor: { rgb: "E8F5E9" } };
                }
                cell.s = style;
              }
            }

            const wb = XLSX.utils.book_new();
            // Sheet reads right-to-left for the Arabic report.
            wb.Workbook = { Views: [{ RTL: !!isArabic }] };
            XLSX.utils.book_append_sheet(wb, ws, this.$t("carOwnersPage.shares"));
            XLSX.writeFile(
              wb,
              "car-owner-shares-" + ownerName + "-" + this.exportFileStamp() + ".xlsx"
            );
          }.bind(this)
        )
        .catch(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("carOwnersPage.failedLoad"), "red");
          }.bind(this)
        )
        .finally(() => (this.sharesExporting = false));
    },
  },
};
</script>

<style scoped>
.totals-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.totals-table td {
  border-bottom: 1px solid #f0f0f0;
  padding: 4px 8px;
}

.cars-link {
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
}
</style>
